import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Clock, Delete, LogIn, LogOut, Coffee, Play, CheckCircle2 } from "lucide-react";

const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit", hour12: false }) : "—");

export default function KioskClock({ standalone }) {
  const [pin, setPin] = useState("");
  const [status, setStatus] = useState(null);   // {member, open, on_break}
  const [done, setDone] = useState(null);

  const check = useMutation({
    mutationFn: async (p) => (await api.get(`/team/clock/status?pin=${p}`)).data,
    onSuccess: (d) => setStatus(d),
    onError: (e) => { toast.error(apiErr(e)); setPin(""); },
  });
  const punch = useMutation({
    mutationFn: async (action) => (await api.post("/team/clock/punch", { pin, action })).data,
    onSuccess: (d) => {
      const labels = { in: "Έναρξη βάρδιας", out: "Λήξη βάρδιας", break_start: "Έναρξη διαλείμματος", break_end: "Λήξη διαλείμματος" };
      setDone({ name: d.member_name, msg: labels[d.action] });
      setStatus(null); setPin("");
      setTimeout(() => setDone(null), 3500);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const press = (d) => {
    const np = (pin + d).slice(0, 4);
    setPin(np);
    if (np.length === 4) check.mutate(np);
  };

  const reset = () => { setPin(""); setStatus(null); };

  return (
    <div className={`max-w-md mx-auto space-y-6 ${standalone ? "min-h-screen px-4 py-10 bg-[#F7F5F1]" : ""}`} data-testid="kiosk-clock-page">
      <div className="text-center">
        <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Team &amp; Time</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 inline-flex items-center gap-2"><Clock className="h-7 w-7" /> Ωρολόγιο</h1>
      </div>

      {done && (
        <div className="rounded-2xl bg-[#E2ECE4] border border-[#BBD2C1] p-6 text-center" data-testid="punch-done">
          <CheckCircle2 className="h-10 w-10 text-[#3F6B4C] mx-auto mb-2" />
          <div className="font-serif text-xl text-[#2f5438]">{done.name}</div>
          <div className="text-sm text-[#3F6B4C]">{done.msg}</div>
        </div>
      )}

      {!status && !done && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-6">
          <p className="text-center text-sm text-[#6F6A60] mb-3">Εισάγετε το 4ψήφιο PIN σας</p>
          <div className="flex justify-center gap-2 mb-5" data-testid="pin-display">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={`h-4 w-4 rounded-full ${i < pin.length ? "bg-[#45423C]" : "bg-[#E3DED4]"}`} />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <button key={n} data-testid={`pin-${n}`} onClick={() => press(String(n))} className="h-16 rounded-xl border border-[#E3DED4] bg-white text-2xl font-serif text-[#45423C] hover:bg-[#F7F4EE] active:scale-95 transition-transform">{n}</button>
            ))}
            <button data-testid="pin-clear" onClick={reset} className="h-16 rounded-xl border border-[#E3DED4] text-[#A1493A] text-sm font-semibold">Καθαρισμός</button>
            <button data-testid="pin-0" onClick={() => press("0")} className="h-16 rounded-xl border border-[#E3DED4] bg-white text-2xl font-serif text-[#45423C] hover:bg-[#F7F4EE] active:scale-95 transition-transform">0</button>
            <button data-testid="pin-back" onClick={() => setPin(pin.slice(0, -1))} className="h-16 rounded-xl border border-[#E3DED4] text-[#6F6A60] flex items-center justify-center"><Delete className="h-6 w-6" /></button>
          </div>
        </div>
      )}

      {status?.member && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-6 space-y-4" data-testid="clock-actions">
          <div className="text-center">
            <div className="font-serif text-2xl text-[#45423C]">{status.member.name}</div>
            <div className="text-sm text-[#6F6A60] mt-1">
              {status.open ? <>Σε βάρδια από {hhmm(status.open.clock_in)}{status.on_break ? " · σε διάλειμμα" : ""}</> : "Εκτός βάρδιας"}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {!status.open && <button data-testid="act-in" onClick={() => punch.mutate("in")} className="col-span-2 h-14 rounded-xl bg-[#6E7A63] text-white font-semibold inline-flex items-center justify-center gap-2"><LogIn className="h-5 w-5" /> Έναρξη βάρδιας</button>}
            {status.open && !status.on_break && <button data-testid="act-break-start" onClick={() => punch.mutate("break_start")} className="h-14 rounded-xl border border-[#E3DED4] text-[#9A6A3A] font-semibold inline-flex items-center justify-center gap-2"><Coffee className="h-5 w-5" /> Διάλειμμα</button>}
            {status.open && status.on_break && <button data-testid="act-break-end" onClick={() => punch.mutate("break_end")} className="h-14 rounded-xl border border-[#E3DED4] text-[#6E7A63] font-semibold inline-flex items-center justify-center gap-2"><Play className="h-5 w-5" /> Λήξη διαλείμματος</button>}
            {status.open && <button data-testid="act-out" onClick={() => punch.mutate("out")} className="h-14 rounded-xl bg-[#45423C] text-white font-semibold inline-flex items-center justify-center gap-2"><LogOut className="h-5 w-5" /> Λήξη βάρδιας</button>}
          </div>
          <button data-testid="kiosk-cancel" onClick={reset} className="w-full text-sm text-[#8A8577] py-2">Ακύρωση</button>
        </div>
      )}
    </div>
  );
}
